/* global HTMLDivElement, HTMLSelectElement, MouseEvent, Node */
import { Children, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode, SelectHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

type BentoSelectOption = {
  value: string;
  label: string;
  disabled: boolean;
};

function optionLabel(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(optionLabel).join("");
  }
  if (isValidElement(node)) {
    return optionLabel((node.props as { children?: ReactNode }).children);
  }
  return "";
}

function selectOptions(children: ReactNode): BentoSelectOption[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) {
      return [];
    }
    const props = (child as ReactElement<{ value?: unknown; children?: ReactNode; disabled?: boolean }>).props;
    const label = optionLabel(props.children).trim();
    return [{
      value: String(props.value ?? label),
      label,
      disabled: Boolean(props.disabled),
    }];
  });
}

/** Shared bento custom select: native select is kept hidden for form/test compatibility. */
export function BentoSelect({ wrapClassName = "", className = "", children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { wrapClassName?: string }) {
  const {
    id,
    value,
    defaultValue,
    onChange,
    disabled,
    name,
    required,
    ...selectProps
  } = props;
  const options = useMemo(() => selectOptions(children), [children]);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => String(defaultValue ?? options[0]?.value ?? ""));
  const currentValue = String(isControlled ? value : internalValue);
  const selected = options.find((option) => option.value === currentValue) ?? options[0];
  const enabledOptions = options.filter((option) => !option.disabled);
  const generatedId = useId();
  const listboxId = `${id ?? generatedId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLSelectElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeValue, setActiveValue] = useState(() => selected?.value ?? "");
  const [menuPosition, setMenuPosition] = useState<{
    left: number; top?: number; bottom?: number; width: number; maxHeight: number;
  }>({ left: 0, top: 0, width: 0, maxHeight: 240 });

  useEffect(() => {
    if (!open) {
      setActiveValue(selected?.value ?? "");
    }
  }, [open, selected?.value]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const positionMenu = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
      const available = Math.max(120, (openUp ? spaceAbove : spaceBelow) - 8);
      const maxHeight = Math.min(260, available);
      // Anchor by the edge nearest the trigger so the panel always sits snug:
      // opening up → pin its BOTTOM just above the trigger (it grows upward by its
      // real content height, instead of floating detached as when `top` was derived
      // from maxHeight). Opening down → pin its TOP just below the trigger.
      setMenuPosition(
        openUp
          ? { left: Math.max(8, rect.left), bottom: window.innerHeight - rect.top + gap, width: rect.width, maxHeight }
          : { left: Math.max(8, rect.left), top: rect.bottom + gap, width: rect.width, maxHeight },
      );
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  function emitChange(nextValue: string) {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (nativeRef.current) {
      nativeRef.current.value = nextValue;
      onChange?.({
        target: nativeRef.current,
        currentTarget: nativeRef.current,
      } as ChangeEvent<HTMLSelectElement>);
    }
  }

  function choose(nextValue: string) {
    const next = options.find((option) => option.value === nextValue);
    if (!next || next.disabled || disabled) {
      return;
    }
    emitChange(next.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function moveActive(direction: 1 | -1) {
    if (enabledOptions.length === 0) return;
    const currentIndex = enabledOptions.findIndex((option) => option.value === (open ? activeValue : currentValue));
    const start = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (start + direction + enabledOptions.length) % enabledOptions.length;
    setActiveValue(enabledOptions[nextIndex].value);
    setOpen(true);
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
      return;
    }
    if (event.key === "Home" && enabledOptions[0]) {
      event.preventDefault();
      setActiveValue(enabledOptions[0].value);
      setOpen(true);
      return;
    }
    if (event.key === "End" && enabledOptions[enabledOptions.length - 1]) {
      event.preventDefault();
      setActiveValue(enabledOptions[enabledOptions.length - 1].value);
      setOpen(true);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (open) {
        choose(activeValue);
      } else {
        setOpen(true);
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  function handleNativeChange(event: ChangeEvent<HTMLSelectElement>) {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  }

  return (
    <div ref={rootRef} className={`group relative ${wrapClassName}`}>
      <select
        {...selectProps}
        ref={nativeRef}
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        value={currentValue}
        onChange={handleNativeChange}
        onFocus={() => triggerRef.current?.focus()}
        tabIndex={-1}
        className="sr-only"
      >
        {children}
      </select>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((next) => !next);
        }}
        onKeyDown={handleTriggerKeyDown}
        className={`bento-select flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-ink-200 bg-white/80 px-3.5 py-2.5 pr-2 text-left text-[13px] font-semibold text-ink-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] outline-none transition duration-150 hover:border-ink-300 hover:bg-white focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-100/70 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-none ${className}`}
      >
        <span className="min-w-0 flex-1 truncate">{selected?.label ?? ""}</span>
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-ink-500 shadow-sm ring-1 ring-ink-200 transition group-hover:text-brand-600 group-focus-within:bg-brand-50 group-focus-within:text-brand-600 group-focus-within:ring-brand-100">
          <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          style={{
            left: menuPosition.left,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
          }}
          className="fixed z-[90] overflow-auto rounded-xl border border-ink-200 bg-white/98 p-1.5 shadow-[0_20px_60px_rgba(31,29,43,0.18)] ring-1 ring-white/70 backdrop-blur"
        >
          {options.map((option) => {
            const isSelected = option.value === currentValue;
            const isActive = option.value === activeValue;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={option.disabled}
                onMouseEnter={() => setActiveValue(option.value)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option.value)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] font-semibold transition ${
                  isSelected
                    ? "bg-brand-50 text-brand-600"
                    : isActive
                      ? "bg-ink-200/55 text-ink-900"
                      : "text-ink-700 hover:bg-ink-200/45 hover:text-ink-900"
                } disabled:cursor-not-allowed disabled:opacity-45`}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
